import { createRef, useCallback, useRef } from 'react';

import { CalendarIcon, CircleMinusIcon, CirclePlusIcon } from 'lucide-react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import HolidaysEmpty from './HolidaysEmpty';

import styles from './BusinessHour.module.scss';

const Holidays = ({
  fields,
  inputClassName,
  handleCreate,
  control,
  errors,
  setShowDatePicker,
  showDatePicker,
  buttonText,
  handleCalendarSave,
  handleDelete,
}) => {
  const { t } = useTranslation();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'business_hours', actions.WRITE)
  );

  const handleButtonClick = useCallback(
    (index) => {
      setShowDatePicker((prev) => {
        const newArray = [...prev];
        newArray[index] = { isOpen: !prev[index].isOpen };
        return newArray;
      });
    },
    [setShowDatePicker]
  );

  const optionRefs = useRef([]);
  optionRefs.current = fields.map((_, i) => optionRefs.current[i] ?? createRef());

  const handleRangeSelect = useCallback(
    (
      range: { from?: Date; to?: Date } | undefined,
      index: number,
      onChange: (value: { startDate: Date; endDate: Date }) => void
    ) => {
      if (range?.from && range?.to) {
        onChange({ startDate: range.from, endDate: range.to });
      } else if (range?.from) {
        onChange({ startDate: range.from, endDate: range.from });
      }
    },
    []
  );

  return (
    <>
      {fields.length === 0 && <HolidaysEmpty onClick={handleCreate} disabled={!canWrite} />}
      {fields.length > 0 && (
        <>
          <div className={styles.titleWrapper}>
            <span>{t('common.name')}</span>
            <span>{t('common.date')}</span>
          </div>
          <div>
            {fields.map((item, index) => {
              return (
                <div className={styles.wrapper} key={item.id}>
                  <div>
                    <Controller
                      render={({ field: { onChange, value, ref } }) => (
                        <form autoComplete="off">
                          <Input
                            id={fields.index?.holidayName}
                            placeholder={t('common.name_placeholder')}
                            className={cn(
                              inputClassName,
                              errors.holidays?.[index]?.holidayName
                                ? 'border-destructive'
                                : 'border-stroke'
                            )}
                            onChange={onChange}
                            value={value}
                            ref={ref}
                            disabled={!canWrite}
                          />
                        </form>
                      )}
                      control={control}
                      name={`holidays.${index}.holidayName`}
                    />
                    <div className={styles.error}>
                      {errors.holidays?.[index]?.holidayName?.message}
                    </div>
                  </div>

                  <div className={styles.calendarWrapper}>
                    <Popover
                      open={showDatePicker[index]?.isOpen}
                      onOpenChange={() => handleButtonClick(index)}
                    >
                      <PopoverTrigger asChild>
                        <Button variant="outline-neutral" className="w-full" disabled={!canWrite}>
                          <CalendarIcon size={16} />
                          <p className={styles.button_text}>{buttonText(index)}</p>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="bg-background p-0"
                        align="start"
                        style={{ width: 'var(--radix-popover-trigger-width)' }}
                      >
                        <div className="p-2">
                          <Controller
                            render={({ field: { onChange, value, ref: _ref } }) => {
                              const selectedRange =
                                value?.startDate && value?.endDate
                                  ? { from: new Date(value.startDate), to: new Date(value.endDate) }
                                  : undefined;

                              return (
                                <>
                                  <Calendar
                                    mode="range"
                                    selected={selectedRange}
                                    onSelect={(range) => handleRangeSelect(range, index, onChange)}
                                    numberOfMonths={1}
                                    className="text-sm"
                                    defaultMonth={new Date(new Date().getFullYear(), 0)} // Start at January of current year
                                    locale={undefined} // Use English locale
                                    formatters={{
                                      formatCaption: (month) => {
                                        return month.toLocaleDateString('en-US', { month: 'long' });
                                      },
                                    }}
                                    startMonth={new Date(new Date().getFullYear(), 0)}
                                    endMonth={new Date(new Date().getFullYear(), 11)}
                                  />
                                  <div className="flex justify-end bg-background p-3">
                                    <Button
                                      size="sm"
                                      onClick={(e) => {
                                        handleCalendarSave(e, index);
                                        // Explicitly close the popover
                                        setShowDatePicker((prev) => {
                                          const newArray = [...prev];
                                          newArray[index] = { isOpen: false };
                                          return newArray;
                                        });
                                      }}
                                      disabled={!canWrite}
                                      className="px-4 py-2"
                                    >
                                      {t('common.save')}
                                    </Button>
                                  </div>
                                </>
                              );
                            }}
                            control={control}
                            name={`holidays.${index}.dates`}
                          />
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <Button
                    variant="ghost-neutral"
                    size="icon-sm"
                    aria-label={t('common.create')}
                    onClick={handleCreate}
                    disabled={!canWrite}
                  >
                    <CirclePlusIcon size={16} />
                  </Button>

                  <Button
                    variant="ghost-neutral"
                    size="icon-sm"
                    aria-label={t('common.delete')}
                    onClick={() => handleDelete(index)}
                    disabled={!canWrite}
                  >
                    <CircleMinusIcon size={16} />
                  </Button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default Holidays;
